import type { ObjectShape } from 'yup/lib/object';
import * as Yup from 'yup';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as ibantools from 'ibantools';

// MangoPay forbids these characters in recipient data: ( ) & , . : _ /
const noForbiddenCharsRegex = /^[^()&,.:_/]*$/;

export const useSetupMangopayDirectDebitValidationSchema = (): ObjectShape => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      firstName: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.firstName'))
        .max(255, t('mangopay.recipient.errors.LENGTH_MORE_THAN_MAX'))
        .matches(
          noForbiddenCharsRegex,
          t('mangopay.recipient.errors.forbidden-chars', {
            chars: '( ) & , . : _ /',
          })
        ),
      lastName: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.lastName'))
        .max(255, t('mangopay.recipient.errors.LENGTH_MORE_THAN_MAX'))
        .matches(
          noForbiddenCharsRegex,
          t('mangopay.recipient.errors.forbidden-chars', {
            chars: '( ) & , . : _ /',
          })
        ),
      iban: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.iban'))
        .test({
          name: 'check-iban',
          test: (rawIban, context) => {
            const iban = rawIban ?? '';
            const isValidIban =
              ibantools.isValidIBAN(iban) ||
              ibantools.isValidIBAN(ibantools.electronicFormatIBAN(iban) ?? '');
            return (
              isValidIban ||
              context.createError({
                message: t('mangopay.direct-debit.setup.valid.iban'),
              })
            );
          },
        })
        .test({
          name: 'check-iban-country',
          test: (rawIban, context) => {
            const iban = rawIban ?? '';
            const country = context.parent.country;

            // Skip validation if IBAN or country is empty
            if (!iban || !country) {
              return true;
            }

            // Extract country code from IBAN (first 2 characters)
            const electronicIban = ibantools.electronicFormatIBAN(iban) ?? iban;
            const extractResult = ibantools.extractIBAN(electronicIban);
            const ibanCountry = extractResult.countryCode;

            // Skip validation if we couldn't extract a valid country code
            if (!ibanCountry) {
              return true;
            }

            // Check if IBAN country matches selected country
            if (ibanCountry.toUpperCase() !== country.toUpperCase()) {
              return context.createError({
                message: t(
                  'mangopay.recipient.errors.IBAN_DOES_NOT_CORRESPOND_TO_ACCOUNT_COUNTRY'
                ),
              });
            }

            return true;
          },
        }),
      addressLine1: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.address-line-1'))
        .matches(
          noForbiddenCharsRegex,
          t('mangopay.recipient.errors.forbidden-chars', {
            chars: '( ) & , . : _ /',
          })
        ),
      addressLine2: Yup.string().matches(
        noForbiddenCharsRegex,
        t('mangopay.recipient.errors.forbidden-chars', {
          chars: '( ) & , . : _ /',
        })
      ),
      city: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.city'))
        .matches(
          noForbiddenCharsRegex,
          t('mangopay.recipient.errors.forbidden-chars', {
            chars: '( ) & , . : _ /',
          })
        ),
      country: Yup.string().required(
        t('mangopay.direct-debit.setup.required.country')
      ),
      postalCode: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.postal-code'))
        .matches(/^\d+$/, t('mangopay.direct-debit.setup.valid.postal-code')),
    }),
    [t]
  );
};
